/**
 * Redeem Code Modal Component
 * Allows users to redeem promo codes for credits
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './RedeemCodeModal.css';

interface RedeemCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RedeemCodeModal: React.FC<RedeemCodeModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { refreshBalance } = useCredits();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ credits: number; newBalance: number; description: string } | null>(null);

  const handleRedeem = async () => {
    if (!user?.id || !code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/credits/redeem-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to redeem code');
      }

      setSuccess({
        credits: data.credits,
        newBalance: data.newBalance,
        description: data.description,
      });
      setCode('');

      // Refresh balance in context
      await refreshBalance();
    } catch (err) {
      console.error('[RedeemCodeModal] Redeem error:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleRedeem();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="redeem-code-overlay" onClick={handleClose}>
      <div className="redeem-code-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="redeem-code-header">
          <div className="header-title">
            <CardGiftcardIcon />
            <span>Redeem Promo Code</span>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="redeem-code-content">
          {success ? (
            <div className="success-message">
              <CheckCircleIcon className="success-icon" />
              <h3>Code Redeemed!</h3>
              <p className="credits-awarded">+{success.credits} credits</p>
              <p className="description">{success.description}</p>
              <p className="new-balance">New balance: {success.newBalance} credits</p>
              <button className="done-btn" onClick={handleClose}>
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="instructions">
                Enter your promo code below to add credits to your account.
              </p>

              <div className="input-group">
                <input
                  type="text"
                  className="code-input"
                  placeholder="XXXX-XXXX-XXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  autoFocus
                  maxLength={14}
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button
                className="redeem-btn"
                onClick={handleRedeem}
                disabled={loading || !code.trim()}
              >
                {loading ? 'Redeeming...' : 'Redeem Code'}
              </button>

              <p className="hint">
                Promo codes are case-insensitive and can include hyphens
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RedeemCodeModal;
